import { getBigIntx1e6, sleep, strToHex, toJson } from '@/src/lib/MayzSmartDB';
import { TokensParticipation, getTotalOfUnitInUTxOList } from '@/src/lib/MayzSmartDB/Commons/index';
import { DelegationEntity, EmulatorEntity, SwapOfferEntity } from '@/src/lib/MayzSmartDB/Entities/index';
import { DelegationApi, FundApi, ProtocolApi, SwapOfferApi, WalletApi } from '@/src/lib/MayzSmartDB/FrontEnd/index';
import {
    CONNECT_WALLET_WAIT_FOR_API_WALLETS_MS,
    CONNECT_WALLET_WAIT_FOR_EXTENSIONS_POOL_WALLETS_MS,
    LUCID_NETWORK_CUSTOM_NAME,
    LUCID_NETWORK_MAINNET_ID,
    LUCID_NETWORK_TESTNET_ID,
    isEmulator,
    isMainnet,
    isTestnet,
} from '@/src/lib/SmartDB/Commons/Constants/constants';
import { CARDANO_WALLETS } from '@/src/lib/SmartDB/Commons/Constants/wallets';
import { explainError } from '@/src/lib/SmartDB/Commons/explainError';
import { EmulatorDBFrontEndApiCalls } from '@/src/lib/SmartDB/FrontEnd/ApiCalls/Emulator.FrontEnd.Api.Calls';
import { AuthApi, Credentials } from '@/src/lib/SmartDB/lib/Auth/index';
import { LucidToolsFrontEnd } from '@/src/lib/SmartDB/lib/Lucid/index';
import { CardanoWallet, ConnectedWalletInfo } from '@/src/types/index';
import { TOKEN_GOV_CS, TOKEN_GOV_TN_STR } from '@/src/utils/constants';
import { LucidEvolution, UTxO, getAddressDetails } from '@lucid-evolution/lucid';
import { Action, Computed, Thunk, action, computed, thunk } from 'easy-peasy';
import { User } from 'next-auth/core/types';
import { signIn, signOut } from 'next-auth/react';

//------------------------------------

export interface IWalletStoreModel {
    emulatorDB?: EmulatorEntity;
    setEmulatorDB: Action<IWalletStoreModel, EmulatorEntity>;

    cardanoWallets: CardanoWallet[];
    setCardanoWallets: Action<IWalletStoreModel, CardanoWallet[]>;

    isGettingWalletsDone: boolean;
    setIsGettingWalletsDone: Action<IWalletStoreModel, boolean>;

    getCardanoWallets: Thunk<IWalletStoreModel, undefined, any, any, Promise<void>>;

    initWallet: Action<IWalletStoreModel>;

    isConnecting: boolean;
    setIsConnecting: Action<IWalletStoreModel, boolean>;

    isConnected: boolean;
    setIsConnected: Action<IWalletStoreModel, boolean>;

    swDoNotPromtForSigning: boolean;
    setSwDoNotPromtForSigning: Action<IWalletStoreModel, boolean>;

    swHideBalance: boolean;
    setSwHideBalance: Action<IWalletStoreModel, boolean>;

    swProInterface: boolean;
    setSwProInterface: Action<IWalletStoreModel, boolean>;

    _lucid: LucidEvolution | undefined;
    getLucid: Thunk<IWalletStoreModel, { emulatorDB?: EmulatorEntity } | undefined, any, any, Promise<LucidEvolution | undefined>>;
    setLucid: Action<IWalletStoreModel, LucidEvolution>;

    _lucidForUseAsUtils?: LucidEvolution;
    getLucidForUseAsUtils: Thunk<IWalletStoreModel, undefined, any, any, Promise<LucidEvolution | undefined>>;
    setLucidForUseAsUtils: Action<IWalletStoreModel, LucidEvolution>;

    protocolParameters: unknown;
    setProtocolParameters: Action<IWalletStoreModel, unknown>;

    info: ConnectedWalletInfo | undefined;
    setInfo: Action<IWalletStoreModel, ConnectedWalletInfo>;

    getPkh: Computed<IWalletStoreModel, (swUseSession?: boolean, session?: any, status?: any) => string | undefined>;

    isLoadingAnyData: Computed<IWalletStoreModel, boolean>;

    isWalletPrivilegesLoading: boolean;
    setIsLoadingWallePrivileges: Action<IWalletStoreModel, boolean>;
    isWalletPrivilegesLoaded: boolean;
    setIsWalletPrivilegesLoaded: Action<IWalletStoreModel, boolean>;

    loadWalletPrivileges: Thunk<IWalletStoreModel>;

    isWalletDataLoading: boolean;
    setIsWalletDataLoading: Action<IWalletStoreModel, boolean>;
    isWalletDataLoaded: boolean;
    setIsWalletDataLoaded: Action<IWalletStoreModel, boolean>;

    loadWalletData: Thunk<IWalletStoreModel, { swLoadParticipation?: boolean; swLoadSwapOffers?: boolean } | undefined>;

    uTxOsAtWallet: UTxO[];
    setUTxOsAtWallet: Action<IWalletStoreModel, UTxO[]>;
    getUTxOsAtWallet: Computed<IWalletStoreModel, () => UTxO[]>;

    _isCoreTeam: boolean;
    isCoreTeam: Computed<IWalletStoreModel, (swUseSession?: boolean, session?: any, status?: any) => boolean>;
    setIsCoreTeam: Action<IWalletStoreModel, boolean>;

    _isProtocolAdmin: boolean;
    isProtocolAdmin: Computed<IWalletStoreModel, (swUseSession?: boolean, session?: any, status?: any) => boolean>;
    setIsProtocolAdmin: Action<IWalletStoreModel, boolean>;

    _isFundAdmin: boolean;
    isFundAdmin: Computed<IWalletStoreModel, (swUseSession?: boolean, session?: any, status?: any) => boolean>;
    setIsFundAdmin: Action<IWalletStoreModel, boolean>;

    _isTokenGovHolder: boolean;
    isTokenGovHolder: Computed<IWalletStoreModel, () => boolean>;

    setIsTokenGovHolder: Action<IWalletStoreModel, boolean>;

    checkSessionValidity: Thunk<IWalletStoreModel, { user: User; walletInfo: ConnectedWalletInfo; isCoreTeam: boolean; isProtocolAdmin: boolean; isFundAdmin: boolean }>;

    connectWallet: Thunk<
        IWalletStoreModel,
        {
            session: any;
            status: any;
            walletName: string;
            walletSeed?: string;
            walletKey?: string;
            tryAgain: boolean;
            useBlockfrostToSubmit: boolean;
            forceConnect?: boolean;
            emulatorDB?: EmulatorEntity;
            createSignedSession: boolean;
        }
    >;

    disconnectWallet: Thunk<IWalletStoreModel, { session: any; status: any }>;

    delegations?: DelegationEntity[];
    setDelegations: Action<IWalletStoreModel, DelegationEntity[] | undefined>;

    swapOffers?: SwapOfferEntity[];
    setSwapOffers: Action<IWalletStoreModel, SwapOfferEntity[] | undefined>;

    isSwapOffersLoading: boolean;
    setIsSwapOffersLoading: Action<IWalletStoreModel, boolean>;
    isSwapOffersLoaded: boolean;
    setIsSwapOffersLoaded: Action<IWalletStoreModel, boolean>;

    loadSwapOffers: Thunk<IWalletStoreModel>;

    isParticipationLoading: boolean;
    setIsParticipationLoading: Action<IWalletStoreModel, boolean>;
    isParticipationLoaded: boolean;
    setIsParticipationLoaded: Action<IWalletStoreModel, boolean>;

    initLoadingParticipation: Thunk<IWalletStoreModel>;
    swLoadParticipation: boolean;
    setSwLoadParticipation: Action<IWalletStoreModel, boolean>;
    finishLoadingParticipation: Thunk<IWalletStoreModel, { allTokensFTWithFundName: Partial<TokensParticipation>[] }>;

    tokenGov: bigint | undefined;
    setTokenGov: Action<IWalletStoreModel, bigint | undefined>;

    tokensFT?: TokensParticipation[];
    setTokensFT: Action<IWalletStoreModel, TokensParticipation[]>;

    getTotalStaked: Computed<IWalletStoreModel, bigint | undefined>;
    getTotalValuation: Computed<IWalletStoreModel, bigint | undefined>;

    getTotalOfUnit: Computed<IWalletStoreModel, (unit: string, swOnlyAvailable?: boolean) => bigint>;
    getTotalNumberOfTokens: Computed<IWalletStoreModel, number>;
}

export const WalletStoreModel: IWalletStoreModel = {
    emulatorDB: undefined,
    setEmulatorDB: action((state, payload) => {
        state.emulatorDB = payload;
    }),

    cardanoWallets: CARDANO_WALLETS,
    setCardanoWallets: action((state, cardanoWallets) => {
        state.cardanoWallets = cardanoWallets;
    }),

    // si es emulador no tiene que cargar wallets
    isGettingWalletsDone: isEmulator,
    setIsGettingWalletsDone: action((state, isGettingWalletsDone) => {
        state.isGettingWalletsDone = isGettingWalletsDone;
    }),

    getCardanoWallets: thunk(async (actions, payload, helpers) => {
        const pollWallets = async (pollWalletsCount: number = 0): Promise<void> => {
            let walletsFound = [];
            if (window.cardano !== undefined) {
                for (const key in window.cardano) {
                    if (window.cardano[key].enable! && walletsFound.indexOf(key) === -1) {
                        walletsFound.push(key);
                    }
                }
            }

            // Proceed if wallets are found or if it's the third attempt
            if (walletsFound.length > 0 || pollWalletsCount === 2) {
                // Get current cardanoWallets from state
                const state = helpers.getState();
                const cardanoWallets = state.cardanoWallets;

                // Update isInstalled flags for found wallets
                walletsFound.forEach((walletName) => {
                    const wallet = cardanoWallets.find((w) => w.wallet === walletName);
                    if (wallet) {
                        wallet.isInstalled = true;
                    }
                });
                // Sort and set new state
                cardanoWallets.sort((a, b) => {
                    // First, sort by isInstalled status
                    if (a.isInstalled && !b.isInstalled) {
                        return -1; // a comes first
                    } else if (!a.isInstalled && b.isInstalled) {
                        return 1; // b comes first
                    } else {
                        // If both have the same isInstalled status, sort alphabetically by name
                        // Assuming the property to sort by is 'name' instead of 'wallet'
                        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                    }
                });
                actions.setCardanoWallets([...cardanoWallets]);
                actions.setIsGettingWalletsDone(true);
            } else if (pollWalletsCount < 2) {
                // Use 2 since count starts from 0 (0, 1, 2)
                setTimeout(() => {
                    pollWallets(pollWalletsCount + 1);
                }, CONNECT_WALLET_WAIT_FOR_EXTENSIONS_POOL_WALLETS_MS);
            } else {
                // Ensure setIsGettingWalletsDone is called even when no wallets are found
                const state = helpers.getState();
                const cardanoWallets = state.cardanoWallets;
                // Sort and set new state
                cardanoWallets.sort((a, b) => {
                    // First, sort by isInstalled status
                    if (a.isInstalled && !b.isInstalled) {
                        return -1; // a comes first
                    } else if (!a.isInstalled && b.isInstalled) {
                        return 1; // b comes first
                    } else {
                        // If both have the same isInstalled status, sort alphabetically by name
                        // Assuming the property to sort by is 'name' instead of 'wallet'
                        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                    }
                });
                actions.setCardanoWallets([...cardanoWallets]);
                actions.setIsGettingWalletsDone(true);
            }
        };
        if (!isEmulator) {
            await pollWallets();
        }
    }),

    initWallet: action((state) => {
        console.log(`[WalletStore] - initWallet`);

        // state.emulatorDB = undefined;

        state.swDoNotPromtForSigning = false;

        state.swHideBalance = true;
        state.isConnecting = false;
        state.isConnected = false;
        state._lucid = undefined;
        state.protocolParameters = undefined;
        state.info = undefined;

        state.isWalletPrivilegesLoading = false;
        state.isWalletPrivilegesLoaded = false;

        state.isWalletDataLoading = false;
        state.isWalletDataLoaded = false;

        state.uTxOsAtWallet = [];
        state._isCoreTeam = false;
        state._isProtocolAdmin = false;
        state._isFundAdmin = false;
        state._isTokenGovHolder = false;

        state.tokenGov = 0n;

        state.delegations = undefined;

        state.swapOffers = undefined;
        state.isSwapOffersLoading = false;

        state.tokensFT = undefined;

        state.swLoadParticipation = false;
        state.isParticipationLoading = false;
        state.isParticipationLoaded = false;
    }),

    swDoNotPromtForSigning: false,
    setSwDoNotPromtForSigning: action((state, swDoNotPromtForSigning) => {
        state.swDoNotPromtForSigning = swDoNotPromtForSigning;
    }),

    swHideBalance: true,
    setSwHideBalance: action((state, swHideBalance) => {
        state.swHideBalance = swHideBalance;
    }),

    swProInterface: false,
    setSwProInterface: action((state, swProInterface) => {
        state.swProInterface = swProInterface;
    }),

    isConnecting: false,
    setIsConnecting: action((state, isConnecting) => {
        state.isConnecting = isConnecting;
    }),

    isConnected: false,
    setIsConnected: action((state, isConnected) => {
        state.isConnected = isConnected;
    }),

    _lucid: undefined,
    setLucid: action((state, newLucid) => {
        state._lucid = newLucid;
        // console.log(`[WalletStore] - setLucid - Lucid Network: ${newLucid.config().network}`);
    }),

    getLucid: thunk(async (actions, payload = {}, helpers): Promise<LucidEvolution | undefined> => {
        //-------------------------
        // normalmente lucid contiene el provider del emulador siempre actualizado en la misma ventan
        // pero si el emulador fue usado en otro lado, de esta forma me aseguro que se lea de la base de datos y se actualice en lucid
        //-------------------------
        let { emulatorDB } = payload;
        //-------------------------
        let state = helpers.getState();
        //-------------------------
        let lucid = state._lucid;
        //-------------------------
        if (lucid !== undefined) {
            // console.log(`[WalletStore] - getLucid - Lucid Network: ${lucid.config().network}`);
            if (isEmulator) {
                if (emulatorDB === undefined) {
                    console.log(`[WalletStore] - getLucid - Getting last updated emulatorDB...`);
                    emulatorDB = await EmulatorDBFrontEndApiCalls.getOneByParamsApi<EmulatorEntity>(EmulatorEntity, { current: true });
                }
                if (emulatorDB === undefined) {
                    throw 'emulatorDB current not defined';
                } else {
                    lucid.switchProvider(emulatorDB.emulator);
                    // NOTE: tengo que reconectar billetera en emulador, por que adentro la billetera tiene provider y el provider de lucid se cambia y no se cambia el de adentro a enos que no recoencte la billetera
                    if (state.info?.walletKey !== undefined) {
                        let walletKey = state.info.walletKey;
                        lucid.selectWallet.fromPrivateKey(walletKey);
                    }
                    actions.setLucid(lucid);
                    actions.setEmulatorDB(emulatorDB);
                }
            }
        }
        return lucid;
    }),

    _lucidForUseAsUtils: undefined,

    setLucidForUseAsUtils: action((state, payload) => {
        state._lucidForUseAsUtils = payload;
    }),

    getLucidForUseAsUtils: thunk(async (actions, payload, helpers): Promise<LucidEvolution | undefined> => {
        // normalmente lucid contiene el provider del emulador siempre actualizado en la misma ventan
        // pero si el emulador fue usado en otro lado, de esta forma me aseguro que se lea de la base de datos y se actualice en lucid
        //-------------------------
        let state = helpers.getState();
        //-------------------------
        let lucid = state._lucidForUseAsUtils;
        //-------------------------
        if (lucid !== undefined) {
            if (isEmulator) {
                console.log(`[WalletStore] - getLucidForUseAsUtils - Getting last updated emulatorDB...`);
                let emulatorDB = await EmulatorDBFrontEndApiCalls.getOneByParamsApi<EmulatorEntity>(EmulatorEntity, { current: true });
                if (emulatorDB === undefined) {
                    throw 'emulatorDB current not defined';
                } else {
                    lucid.switchProvider(emulatorDB.emulator);
                    actions.setLucidForUseAsUtils(lucid);
                }
            }
        }
        return lucid;
    }),

    protocolParameters: undefined,
    setProtocolParameters: action((state, newProtocolParameters) => {
        state.protocolParameters = newProtocolParameters;
    }),

    info: undefined,
    setInfo: action((state, newInfo) => {
        state.info = newInfo;
    }),

    getPkh: computed((state) => (swUseSession = false, session: any, status: any): string | undefined => {
        if (state.isConnected && state.info) {
            return state.info.pkh;
        } else if (swUseSession === true && status === 'authenticated') {
            if (session && session.user) {
                return session.user.pkh;
            }
        }
        return undefined;
    }),

    isLoadingAnyData: computed((state) => {
        return state.isWalletPrivilegesLoading || state.isWalletDataLoading || state.isParticipationLoading || state.isSwapOffersLoading;
    }),

    loadWalletPrivileges: thunk(async (actions, _, helpers) => {
        const state = helpers.getState();
        console.log(`[WalletStore] - loadWalletPrivileges - Init`);
        try {
            if (state._lucid && state.info) {
                actions.setIsWalletPrivilegesLoaded(false);
                actions.setIsLoadingWallePrivileges(true);
                const isCoreTeam = await WalletApi.isCoreTeamApi(state.info.pkh);
                actions.setIsCoreTeam(isCoreTeam ? true : false);
                const isProtocolAdmin = await ProtocolApi.isAdminApi(state.info.pkh);
                actions.setIsProtocolAdmin(isProtocolAdmin ? true : false);
                const isFundAdmin = await FundApi.isAdminApi(state.info.pkh);
                actions.setIsFundAdmin(isFundAdmin ? true : false);
                actions.setIsWalletPrivilegesLoaded(true);
                actions.setIsLoadingWallePrivileges(false);
                console.log(`[WalletStore] - loadWalletPrivileges - OK`);
            }
        } catch (error) {
            console.log(`[WalletStore] - loadWalletPrivileges - Error: ${error}`);
            actions.setIsLoadingWallePrivileges(false);
            throw error;
        }
    }),

    loadWalletData: thunk(async (actions, payload, helpers) => {
        //----------
        const swLoadParticipation = payload?.swLoadParticipation ?? true;
        const swLoadSwapOffers = payload?.swLoadSwapOffers ?? false;
        //----------
        const state = helpers.getState();
        //----------
        console.log(`[WalletStore] - loadWalletData Init`);
        try {
            if (state.isConnected && state._lucid && state.info) {
                actions.setIsWalletDataLoaded(false);
                actions.setIsWalletDataLoading(true);
                const lucid = await actions.getLucid();
                let uTxOsAtWallet: UTxO[] = [];
                try {
                    uTxOsAtWallet = await lucid!.wallet().getUtxos();
                } catch (error) {}
                console.log('[WalletStore] - loadWalletData - uTxOsAtWallet: ' + uTxOsAtWallet.length);
                actions.setUTxOsAtWallet(uTxOsAtWallet);
                if (uTxOsAtWallet.length === 0) {
                    console.log(`[WalletStore] - loadWalletData: There are no UTxOs available in your Wallet`);
                }
                //--------------------------------------
                const tokenGov_AC_Lucid = TOKEN_GOV_CS + strToHex(TOKEN_GOV_TN_STR);
                //--------------------------------------
                const totalTokenGov = getTotalOfUnitInUTxOList(tokenGov_AC_Lucid, uTxOsAtWallet);
                //--------------------------------------
                actions.setTokenGov(totalTokenGov);
                //--------------------------------------
                const isTokenGovHolder = totalTokenGov > 0;
                //--------------------------------------
                actions.setIsTokenGovHolder(isTokenGovHolder);
                //--------------------------------------
                const loadDelegationList = async () => {
                    const params = { ddDelegatorPaymentPKH: state.info!.pkh };
                    const delegations: DelegationEntity[] = await DelegationApi.getByParamsApi_(params);
                    return delegations;
                };
                actions.setDelegations(await loadDelegationList());
                //--------------------------------------
                actions.setIsWalletDataLoaded(true);
                actions.setIsWalletDataLoading(false);
                //--------------------------------------
                if (swLoadParticipation === true) {
                    actions.initLoadingParticipation();
                }
                //--------------------------------------
                if (swLoadSwapOffers === true) {
                    actions.loadSwapOffers();
                }
                //--------------------------------------
                console.log(`[WalletStore] - loadWalletData - OK`);
            }
        } catch (error) {
            console.log(`[WalletStore] - loadWalletData - Error: ${error}`);
            actions.setIsWalletDataLoading(false);
            throw error;
        }
    }),

    isWalletPrivilegesLoading: false,
    setIsLoadingWallePrivileges: action((state, isLoading) => {
        state.isWalletPrivilegesLoading = isLoading;
    }),

    isWalletPrivilegesLoaded: false,
    setIsWalletPrivilegesLoaded: action((state, isLoaded) => {
        state.isWalletPrivilegesLoaded = isLoaded;
    }),

    isWalletDataLoading: false,
    setIsWalletDataLoading: action((state, isLoading) => {
        state.isWalletDataLoading = isLoading;
    }),

    isWalletDataLoaded: false,
    setIsWalletDataLoaded: action((state, isLoaded) => {
        state.isWalletDataLoaded = isLoaded;
    }),

    uTxOsAtWallet: [],
    setUTxOsAtWallet: action((state, newUTxOs) => {
        state.uTxOsAtWallet = newUTxOs;
    }),
    getUTxOsAtWallet: computed((state) => (): UTxO[] => {
        return state.uTxOsAtWallet;
    }),

    _isCoreTeam: false,
    setIsCoreTeam: action((state, isCore) => {
        state._isCoreTeam = isCore;
    }),
    isCoreTeam: computed((state) => (swUseSession?: boolean, session?: any, status?: any) => {
        if (state.isWalletPrivilegesLoaded) {
            return state._isCoreTeam;
        } else if (swUseSession === true && status === 'authenticated') {
            if (session && session.user && session.user.isCoreTeam) {
                return session.user.isCoreTeam;
            }
        }
        return false;
    }),

    _isProtocolAdmin: false,
    setIsProtocolAdmin: action((state, isProtocolAdmin) => {
        state._isProtocolAdmin = isProtocolAdmin;
    }),
    isProtocolAdmin: computed((state) => (swUseSession = false, session: any, status: any) => {
        if (state.isWalletPrivilegesLoaded === true) {
            return state._isProtocolAdmin;
        } else if (swUseSession === true && status === 'authenticated') {
            if (session && session.user && session.user.isProtocolAdmin) {
                return session.user.isProtocolAdmin;
            }
        }
        return false;
    }),

    _isFundAdmin: false,
    setIsFundAdmin: action((state, isFundAdmin) => {
        state._isFundAdmin = isFundAdmin;
    }),
    isFundAdmin: computed((state) => (swUseSession = false, session: any, status: any) => {
        if (state.isWalletPrivilegesLoaded) {
            return state._isFundAdmin;
        } else if (swUseSession === true && status === 'authenticated') {
            if (session && session.user && session.user.isFundAdmin) {
                return session.user.isFundAdmin;
            }
        }
        return false;
    }),

    _isTokenGovHolder: false,
    setIsTokenGovHolder: action((state, isTokenGovHolder) => {
        state._isTokenGovHolder = isTokenGovHolder;
    }),
    isTokenGovHolder: computed((state) => () => {
        if (state.isWalletDataLoaded) {
            return state._isTokenGovHolder;
        }
        return false;
    }),

    checkSessionValidity: thunk(async (actions, payload, helpers) => {
        //------------------------------
        const { user, walletInfo, isCoreTeam, isProtocolAdmin, isFundAdmin } = payload;
        //------------------------------
        const swSettedSessionOk =
            user.address !== undefined &&
            user.pkh !== undefined &&
            (walletInfo.stakePkh === undefined || user.stakePkh !== undefined) &&
            user.walletName !== undefined &&
            user.useBlockfrostToSubmit !== undefined &&
            user.isWalletFromSeed !== undefined &&
            user.isWalletFromKey !== undefined &&
            user.isCoreTeam !== undefined &&
            user.isProtocolAdmin !== undefined &&
            user.isFundAdmin !== undefined &&
            user.network !== undefined &&
            (walletInfo.isWalletValidatedWithSignedToken === undefined || user.isWalletValidatedWithSignedToken !== undefined);
        //------------------------------
        const prevUser = {
            address: user.address,
            pkh: user.pkh,
            stakePkh: user.stakePkh,
            walletName: user.walletName,
            walletSeed: user.walletSeed,
            walletKey: user.walletKey,
            useBlockfrostToSubmit: user.useBlockfrostToSubmit,
            isWalletFromSeed: user.isWalletFromSeed,
            isWalletFromKey: user.isWalletFromKey,
            isCoreTeam: user.isCoreTeam,
            isProtocolAdmin: user.isProtocolAdmin,
            isFundAdmin: user.isFundAdmin,
            network: user.network,
            isWalletValidatedWithSignedToken: user.isWalletValidatedWithSignedToken,
        };
        //------------------------------
        const currentUser = {
            address: walletInfo.address,
            pkh: walletInfo.pkh,
            stakePkh: walletInfo.stakePkh,
            walletName: walletInfo.walletName,
            walletSeed: walletInfo.walletSeed,
            walletKey: walletInfo.walletKey,
            useBlockfrostToSubmit: walletInfo.useBlockfrostToSubmit,
            isWalletFromSeed: walletInfo.isWalletFromSeed,
            isWalletFromKey: walletInfo.isWalletFromKey,
            isCoreTeam: isCoreTeam,
            isProtocolAdmin: isProtocolAdmin,
            isFundAdmin: isFundAdmin,
            network: process.env.NEXT_PUBLIC_CARDANO_NET,
            isWalletValidatedWithSignedToken: walletInfo.isWalletValidatedWithSignedToken,
        };
        //------------------------------
        const swSessionOk =
            prevUser.address === currentUser.address &&
            prevUser.pkh === currentUser.pkh &&
            prevUser.stakePkh === currentUser.stakePkh &&
            prevUser.walletName === currentUser.walletName &&
            prevUser.walletSeed === currentUser.walletSeed &&
            prevUser.walletKey === currentUser.walletKey &&
            prevUser.useBlockfrostToSubmit === currentUser.useBlockfrostToSubmit &&
            prevUser.isWalletFromSeed === currentUser.isWalletFromSeed &&
            prevUser.isWalletFromKey === currentUser.isWalletFromKey &&
            prevUser.isCoreTeam === currentUser.isCoreTeam &&
            prevUser.isProtocolAdmin === currentUser.isProtocolAdmin &&
            prevUser.isFundAdmin === currentUser.isFundAdmin &&
            prevUser.network === currentUser.network &&
            prevUser.isWalletValidatedWithSignedToken === currentUser.isWalletValidatedWithSignedToken;
        //------------------------------
        const isSessionValid = swSessionOk && swSettedSessionOk;
        //------------------------------
        const updatedValues = {};
        Object.entries(prevUser).forEach(([key, value]) => {
            if ((currentUser as any)[key] !== value) {
                (updatedValues as any)[key] = {
                    from: value,
                    to: (currentUser as any)[key],
                };
            }
        });
        //------------------------------
        console.log(
            `[WalletStore] - checkSessionValidity - all set: ${swSettedSessionOk.toString()} - updated: ${swSessionOk.toString()} - which fields: ${
                !swSessionOk ? toJson({ updatedValues }) : null
            }`
        );
        //------------------------------
        return isSessionValid;
    }),

    connectWallet: thunk(async (actions, payload, helpers) => {
        console.log(`[WalletStore] - Connecting Wallet...`);
        //----------------------------
        const { session, status, walletName, walletSeed, walletKey, tryAgain, useBlockfrostToSubmit, forceConnect, createSignedSession } = payload;
        let { emulatorDB } = payload;
        //----------------------------
        const isWalletFromSeed = walletSeed !== undefined;
        const isWalletFromKey = walletKey !== undefined;
        //----------------------------
        let isNewConnection = false;
        //----------------------------
        try {
            let state = helpers.getState();
            //----------------------------
            if (isWalletFromSeed === false && isWalletFromKey === false && window.cardano === undefined) {
                throw `Install a Cardano Wallet and please try again...`;
            }
            //----------------------------
            const isWalletApiEnabled = isWalletFromSeed || isWalletFromKey || (await window.cardano[walletName]?.isEnabled());
            //----------------------------
            const isStoreWalletConnected = state.isConnected && state.info?.walletName === walletName;
            //----------------------------
            let swConnect = forceConnect === true || isWalletApiEnabled === false || isStoreWalletConnected === false;
            //----------------------------
            let isSessionInvalidOrNoExists = undefined;
            if (!swConnect) {
                isSessionInvalidOrNoExists = true;
                if (state.info) {
                    const isSessionValid = await actions.checkSessionValidity({
                        user: session.user,
                        walletInfo: state.info,
                        isCoreTeam: state._isCoreTeam,
                        isProtocolAdmin: state._isProtocolAdmin,
                        isFundAdmin: state._isFundAdmin,
                    });
                    isSessionInvalidOrNoExists = !isSessionValid;
                }
                swConnect = isSessionInvalidOrNoExists;
            }
            //----------------------------
            if (swConnect) {
                //----------------------------
                isNewConnection = true;
                //----------------------------
                if (isEmulator) {
                    if (emulatorDB === undefined) {
                        console.log(`[WalletStore] - Connecting Wallet with Emulator. Getting last updated emulatorDB...`);
                        emulatorDB = await EmulatorDBFrontEndApiCalls.getOneByParamsApi<EmulatorEntity>(EmulatorEntity, { current: true });
                    }
                    if (emulatorDB === undefined) {
                        throw `Emulator engine not ready for connect, please try again...`;
                    }
                }
                //----------------------------
                console.log(
                    `[WalletStore] - Wallet will be connected because its needed: wallet Api not enabled - ${!isWalletApiEnabled} - or wallet not connected - ${!state.isConnected} - or force connect - ${forceConnect} - or invalid session: ${isSessionInvalidOrNoExists}`
                );
                //----------------------------
                actions.initWallet();
                actions.setIsConnecting(true);
                //----------------------------
                let lucid: LucidEvolution | undefined = undefined;
                //----------------------------
                if (isWalletFromSeed || isWalletFromKey) {
                    //----------------------------
                    try {
                        if (isEmulator) {
                            if (isWalletFromSeed) {
                                console.log(`[WalletStore] - Init Lucid with Emulator and Private Key...`);
                                lucid = await LucidToolsFrontEnd.initializeLucidWithEmulatorAndWalletFromSeed(emulatorDB!, walletSeed, {
                                    addressType: 'Base',
                                    accountIndex: 0,
                                });
                            } else if (isWalletFromKey) {
                                console.log(`[WalletStore] - Init Lucid with Emulator and Private Key...`);
                                lucid = await LucidToolsFrontEnd.initializeLucidWithEmulatorAndWalletFromPrivateKey(emulatorDB!, walletKey);
                            }
                        } else {
                            if (isWalletFromSeed) {
                                console.log(`[WalletStore] - Init Lucid with Wallet from Seed...`);
                                lucid = await LucidToolsFrontEnd.initializeLucidWithBlockfrostAndWalletFromSeed(walletSeed, { addressType: 'Base', accountIndex: 0 });
                            } else if (isWalletFromKey) {
                                console.log(`[WalletStore] - Init Lucid with Emulator and Private Key...`);
                                lucid = await LucidToolsFrontEnd.initializeLucidWithBlockfrostAndWalletFromPrivateKey(walletKey);
                            }
                        }
                    } catch (error) {
                        throw error;
                    }
                    //----------------------------
                } else {
                    //----------------------------
                    let walletApi = undefined;
                    if (isWalletApiEnabled === false) {
                        console.log(`[WalletStore] - Enabling Wallet Api...`);
                        let countError = 0;
                        let errorStr = '';
                        const maxError = tryAgain ? 2 : 1;
                        while (countError < maxError) {
                            try {
                                if (countError > 0) {
                                    //espero para que se cargue la wallet
                                    await sleep(CONNECT_WALLET_WAIT_FOR_API_WALLETS_MS);
                                }
                                walletApi = await window.cardano[walletName].enable();
                                break;
                            } catch (error) {
                                console.log('[WalletStore] - Try ' + countError + ' of ' + maxError + ' - Error: ' + error);
                                errorStr = explainError(error);
                                countError++;
                            }
                        }
                        if (!walletApi) {
                            throw errorStr;
                        }
                    } else {
                        console.log(`[WalletStore] - Wallet Api is already enabled`);
                        // deberia tener guardada la wallet api?
                        walletApi = window.cardano[walletName];
                        walletApi = await window.cardano[walletName].enable();
                    }
                    //----------------------------
                    try {
                        console.log(`[WalletStore] - Init Lucid with Wallet Api...`);
                        const networkId = await walletApi.getNetworkId();
                        console.log(`[WalletStore] - NetworkId: ${networkId}`);
                        if (isMainnet && networkId !== LUCID_NETWORK_MAINNET_ID) {
                            throw `Must connect with a Mainnet Cardano Wallet`;
                        }
                        if (isTestnet && networkId !== LUCID_NETWORK_TESTNET_ID) {
                            throw `Must connect with a ${process.env.NEXT_PUBLIC_CARDANO_NET!} Testnet Cardano Wallet`;
                        }
                        lucid = await LucidToolsFrontEnd.initializeLucidWithBlockfrostAndWalletApi(walletApi);
                    } catch (error) {
                        throw error;
                    }
                }
                //----------------------------
                if (lucid === undefined) {
                    throw 'Lucid is not defined';
                }
                //----------------------------
                const addressWallet = await lucid.wallet().address();
                const pkh = getAddressDetails(addressWallet)?.paymentCredential?.hash;
                const stakePkh = getAddressDetails(addressWallet)?.stakeCredential?.hash;
                // const stakePkh = addrToStakePubKeyHash (addressWallet)
                //----------------------------
                if (!pkh) {
                    throw `Can't get Payment Credentials from address`;
                }
                //----------------------------
                console.log('[WalletStore] - Wallet connected with Lucid: pkh: ' + pkh);
                console.log('[WalletStore] - Wallet connected with Lucid: addressWallet: ' + addressWallet);
                console.log('[WalletStore] - Loading Wallet details...');
                //----------------------------
                const protocolParameters = lucid.config().protocolParameters;
                //----------------------------
                // let useBlockfrostToSubmit_ = useBlockfrostToSubmit;
                // if (status === 'authenticated') {
                //     if (session && session.user && session.user.useBlockfrostToSubmit) {
                //         useBlockfrostToSubmit_ = session.user.useBlockfrostToSubmit;
                //     }
                // }
                // //----------------------------
                // //seteo a la fuerza el no enviar por blockfrost, por las dudas de que alguien haya creado ya la session con este campo
                // //y yo lo estoy sacando del formulario, no podria desacativarlo
                // useBlockfrostToSubmit_ = false;
                //----------------------------
                const info: ConnectedWalletInfo = {
                    walletName,
                    walletSeed,
                    walletKey,
                    address: addressWallet,
                    pkh,
                    stakePkh,
                    useBlockfrostToSubmit: useBlockfrostToSubmit,
                    isWalletFromSeed,
                    isWalletFromKey,
                    network: process.env.NEXT_PUBLIC_CARDANO_NET!,
                    isWalletValidatedWithSignedToken: createSignedSession,
                };
                //----------------------------
                if (
                    (lucid.config().network !== process.env.NEXT_PUBLIC_CARDANO_NET && isEmulator === false) ||
                    (lucid.config().network !== LUCID_NETWORK_CUSTOM_NAME && isEmulator === true)
                ) {
                    throw `Connected Wallet is not from the same network as the application ${lucid.config().network} !== ${process.env.NEXT_PUBLIC_CARDANO_NET}`;
                }
                //----------------------------
                actions.setLucid(lucid);
                if (emulatorDB !== undefined) {
                    actions.setEmulatorDB(emulatorDB);
                }
                actions.setProtocolParameters(protocolParameters);
                actions.setInfo(info);
                //----------------------------
                await actions.loadWalletPrivileges();
                //----------------------------
                state = helpers.getState();
                //----------------------------
                console.log('[WalletStore] - Creating or Updating session... - status: ' + status + ' - session.user.pkh: ' + session?.user?.pkh + ' - pkh: ' + pkh);
                //----------------------------
                let swUpdateSession = status !== 'authenticated';
                if (status === 'authenticated' && session && session.user) {
                    //all the fields must be setted in the session.user
                    let isSessionInvalidOrNoExists = true;
                    if (state.info) {
                        const isSessionValid = await actions.checkSessionValidity({
                            user: session.user,
                            walletInfo: state.info,
                            isCoreTeam: state._isCoreTeam,
                            isProtocolAdmin: state._isProtocolAdmin,
                            isFundAdmin: state._isFundAdmin,
                        });
                        isSessionInvalidOrNoExists = !isSessionValid;
                    }
                    swUpdateSession = swUpdateSession || isSessionInvalidOrNoExists;
                }
                //----------------------------
                if (swUpdateSession) {
                    //----------------------------
                    console.log(`[WalletStore] - Need to update Session`);
                    // await signOut({ redirect: false });
                    //--------------------------------------
                    const credentials: Credentials = {
                        address: info.address,
                        walletName: info.walletName,
                        walletSeed: info.walletSeed,
                        walletKey: info.walletKey,
                        useBlockfrostToSubmit: state.info?.useBlockfrostToSubmit ? 'true' : 'false',
                        isWalletFromSeed: state.info?.isWalletFromSeed ? 'true' : 'false',
                        isWalletFromKey: state.info?.isWalletFromKey ? 'true' : 'false',
                    };
                    //--------------------------------------
                    const token = await AuthApi.generateAuthTokensApi(lucid, credentials, createSignedSession);
                    //--------------------------------------
                    console.log(`[WalletStore] - Signin with JWT Token...`);
                    //--------------------------------------
                    const sessionStatus = await signIn('credentials', {
                        token,
                        redirect: false,
                    });
                    //--------------------------------------
                    if (sessionStatus?.ok === false) {
                        throw sessionStatus.error;
                    }
                    //--------------------------------------
                }
                //----------------------------
                actions.setIsConnected(true);
                actions.setIsConnecting(false);
                //----------------------------
                actions.loadWalletData();
                //----------------------------
                console.log(`[WalletStore] - Wallet connected and session OK`);
                //----------------------------
                return isNewConnection;
            } else {
                console.log(`[WalletStore] - Wallet already enabled and connected`);
            }
            //----------------------------
        } catch (error) {
            console.log(`[WalletStore] - connectWallet - Error: ${error}`);
            if (status === 'authenticated') {
                try {
                    await signOut({ redirect: false });
                } catch (error2) {
                    console.log(`[WalletStore] - connectWallet - Error: ${error}`);
                    throw error + ' | ' + error2;
                }
            }
            throw error;
        } finally {
            actions.setIsConnecting(false);
        }
    }),

    disconnectWallet: thunk(async (actions, payload, helpers) => {
        console.log(`[WalletStore] - disconnectWallet - Init`);
        const { session, status } = payload;
        actions.initWallet();
        try {
            if (status === 'authenticated') {
                await signOut({ redirect: false });
            }
        } catch (error) {
            console.log(`[WalletStore] - disconnectWallet - Error: ${error}`);
            throw error;
        }
    }),

    delegations: undefined,

    setDelegations: action((state, payload) => {
        state.delegations = payload;
    }),

    swapOffers: undefined,
    setSwapOffers: action((state, payload) => {
        state.swapOffers = payload;
    }),

    isSwapOffersLoading: false,
    setIsSwapOffersLoading: action((state, isSwapOffersLoading) => {
        state.isSwapOffersLoading = isSwapOffersLoading;
    }),

    isSwapOffersLoaded: false,
    setIsSwapOffersLoaded: action((state, isSwapOffersLoaded) => {
        state.isSwapOffersLoaded = isSwapOffersLoaded;
    }),

    loadSwapOffers: thunk(async (actions, payload, helpers) => {
        //--------
        const state = helpers.getState();
        //--------
        console.log(`[WalletStore] - loadSwapOffers Init`);
        //--------
        let swapOffers: SwapOfferEntity[] = [];
        //--------
        try {
            //--------------------------------------
            const loadSwapOfferList = async () => {
                const params = { sodSellerPaymentPKH: state.info!.pkh };
                const swapOffers: SwapOfferEntity[] = await SwapOfferApi.getByParamsApi_(params);
                return swapOffers;
            };
            //--------------------------------------
            if (state.isConnected && state._lucid && state.info) {
                actions.setIsSwapOffersLoaded(false);
                actions.setIsSwapOffersLoading(true);
                //--------------------------------------
                swapOffers = await loadSwapOfferList();
                //--------------------------------------
                actions.setSwapOffers(swapOffers);
                //--------------------------------------
                actions.setIsSwapOffersLoaded(true);
                actions.setIsSwapOffersLoading(false);
                //--------------------------------------
                console.log(`[WalletStore] - loadSwapOffers - OK`);
                //--------------------------------------
                return swapOffers;
            }
        } catch (error) {
            console.log(`[WalletStore] - loadSwapOffers - Error: ${error}`);
            actions.setIsSwapOffersLoading(false);
            throw error;
        }
    }),

    isParticipationLoading: false,
    setIsParticipationLoading: action((state, isParticipationLoading) => {
        state.isParticipationLoading = isParticipationLoading;
    }),

    isParticipationLoaded: false,
    setIsParticipationLoaded: action((state, isParticipationLoaded) => {
        state.isParticipationLoaded = isParticipationLoaded;
    }),

    swLoadParticipation: false,
    setSwLoadParticipation: action((state, swLoadParticipation) => {
        state.swLoadParticipation = swLoadParticipation;
    }),

    initLoadingParticipation: thunk(async (actions, payload, helpers) => {
        //--------
        let state = helpers.getState();
        //--------
        console.log(`[WalletStore] - initLoadingParticipation - Init`);
        //--------
        if (state.isConnected && state._lucid && state.info) {
            //--------------------------------------
            actions.setIsParticipationLoaded(false);
            actions.setIsParticipationLoading(true);
            //--------------------------------------
            actions.setSwLoadParticipation(true);
            //--------------------------------------
            console.log(`[WalletStore] - initLoadingParticipation - OK`);
            //--------------------------------------
            return;
        }
    }),

    finishLoadingParticipation: thunk(async (actions, payload, helpers) => {
        //--------
        const allTokensFTWithFundName = payload.allTokensFTWithFundName;
        //--------
        let state = helpers.getState();
        //--------
        console.log(`[WalletStore] - finishLoadingParticipation - Init`);
        //--------
        try {
            const calculateParticipation = (): TokensParticipation[] => {
                //--------------------------------------
                const tokens_With_Price_And_Date_And_Signature_And_Metadata_And_FundName_And_Amount: Partial<TokensParticipation>[] = [];
                //--------------------------------------
                for (const token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName of allTokensFTWithFundName) {
                    //--------------------------------------
                    const available_FT_in_Wallet = state.getTotalOfUnit(
                        token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName.CS! + token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName.TN_Hex!
                    );
                    //--------------------------------------
                    if (available_FT_in_Wallet > 0) {
                        //--------------------------------------
                        if (token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName !== undefined) {
                            const token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName_And_Amount: Partial<TokensParticipation> = {
                                ...token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName,
                                amount: available_FT_in_Wallet,
                            };
                            tokens_With_Price_And_Date_And_Signature_And_Metadata_And_FundName_And_Amount.push(
                                token_With_Price_And_Date_And_Signature_And_Metadata_And_FundName_And_Amount
                            );
                        }
                    }
                }
                //----------------------------
                const sortedValues = tokens_With_Price_And_Date_And_Signature_And_Metadata_And_FundName_And_Amount.sort((a, b): number => {
                    const productA = a.amount !== undefined && a.priceADAx1e6 !== undefined ? Number(BigInt(a.amount) * BigInt(a.priceADAx1e6)) : 0;
                    const productB = b.amount !== undefined && b.priceADAx1e6 !== undefined ? Number(BigInt(b.amount) * BigInt(b.priceADAx1e6)) : 0;
                    return productB - productA;
                });
                //----------------------------
                const totalSum: bigint = sortedValues.reduce((sum, token) => {
                    if (token.amount !== undefined && token.priceADAx1e6 !== undefined) {
                        const product = token.amount * token.priceADAx1e6!;
                        return sum + product;
                    } else {
                        return sum;
                    }
                }, BigInt(0));
                //----------------------------
                const tokensParticipation: Partial<TokensParticipation>[] = sortedValues.map((token) => {
                    let percentage = 0n;
                    if (token.amount !== undefined && token.priceADAx1e6 !== undefined) {
                        const product = token.amount * token.priceADAx1e6!;
                        percentage = totalSum !== 0n ? (product * 100n) / totalSum : 0n;
                    }
                    return {
                        ...token,
                        percentage: percentage,
                    };
                });
                //--------------------------------------
                return tokensParticipation as TokensParticipation[];
                //--------------------------------------
            };
            if (state.isConnected && state._lucid && state.info && state.swLoadParticipation === true) {
                //--------------------------------------
                actions.setSwLoadParticipation(false);
                //--------------------------------------
                const tokensParticipation = calculateParticipation();
                //--------------------------------------
                actions.setTokensFT(tokensParticipation);
                //--------------------------------------
                actions.setIsParticipationLoaded(true);
                actions.setIsParticipationLoading(false);
                //--------------------------------------
                console.log(`[WalletStore] - finishLoadingParticipation - OK`);
                //--------------------------------------
                return tokensParticipation;
            }
        } catch (error) {
            console.log(`[WalletStore] - finishLoadingParticipation - Error: ${error}`);
            actions.setIsParticipationLoading(false);
            throw error;
        }
    }),

    tokenGov: undefined,
    setTokenGov: action((state, tokenGov) => {
        state.tokenGov = tokenGov;
    }),

    tokensFT: undefined,
    setTokensFT: action((state, payload) => {
        state.tokensFT = payload;
    }),

    getTotalStaked: computed((state) => {
        const delegations = state.delegations;
        if (delegations === undefined) {
            return 0n;
        }
        const totalStaked = delegations.reduce((accumulated: bigint, delegation) => {
            return accumulated + delegation.ddStaked;
        }, 0n);

        // console.log(`[WalletStore] - Total Staked: ${totalStaked}`);

        return totalStaked;
    }),

    getTotalValuation: computed((state) => {
        const tokensFT = state.tokensFT;
        if (tokensFT === undefined) {
            return 0n;
        }
        const totalValuation = tokensFT.reduce((accumulated: bigint, token) => {
            return accumulated + getBigIntx1e6(token.amount * token.priceADAx1e6!)!;
        }, 0n);

        // console.log(`[WalletStore] - Total valuation: ${totalValuation}`);

        return totalValuation;
    }),

    getTotalOfUnit: computed((state) => (unit: string, swOnlyAvailable: boolean = false) => {
        return getTotalOfUnitInUTxOList(unit, state.uTxOsAtWallet, swOnlyAvailable);
    }),

    getTotalNumberOfTokens: computed((state) => {
        const UTxOs = state.uTxOsAtWallet;

        // Collect all token keys from each UTxO's assets
        const allTokenKeys = UTxOs.reduce((accumulatedKeys: string[], utxo) => {
            const assetKeys = Object.keys(utxo.assets || {});
            return accumulatedKeys.concat(assetKeys);
        }, []);

        // Deduplicate the keys to count unique tokens
        const uniqueTokenKeys = new Set(allTokenKeys);

        // The size of the Set represents the count of unique tokens
        const uniqueTokenCount = uniqueTokenKeys.size;

        // console.log(`[WalletStore] - Total unique tokens: ${uniqueTokenCount}`);

        return uniqueTokenCount;
    }),
};
